/**
 * Channel pipeline halves of `runPendingSave`:
 *
 *  - `runChannelInsertsAppend` materialises each pending channel-drop by
 *    transferring the source image (collection target) and then linking the
 *    resulting asset at a TEMP slot = `maxExistingSlot + 1 + i`. This avoids
 *    the UNIQUE(product_id, channel_id, slot) constraint and the upsert path
 *    that would otherwise REPLACE the occupant at the user's target slot —
 *    which was the 2026-05-21 user-bug root cause.
 *
 *  - `runChannelReorderFinal` then calls `reorderProductImages` once per
 *    affected channel with the FULL final link-id list (existing + newly
 *    inserted, ordered by target slot). The backend stamps slots 1..N from
 *    the input list, so the call must be exhaustive.
 *
 * The two-step append-then-reorder is the bug fix; the AI-reference path is
 * untouched and still goes through the simpler transfer-only route in
 * `runAiRefInserts` (see runPendingSave.ts).
 */
import { linkAssetToChannel, reorderProductImages } from '@/lib/api/products'
import { ensureAssetId } from './ensureAssetId'
import {
  buildFinalLinkIds,
  type ChannelInsert,
  type ChannelSnapshot,
  type PendingShift,
} from './buildReorderPlan'
import type { CurrentSlotsByChannel, PendingDrop } from './usePendingChannelDrops'

export interface ChannelInsertContext {
  productId: string
  pending: Map<string, PendingDrop>
  snapshot: CurrentSlotsByChannel
}

export interface ChannelInsertResult {
  /** pendingKey → newly-created link_id (from linkAssetToChannel response). */
  newLinkIds: Map<string, string>
  ok: number
  err: number
}

function maxSlot(snap: ChannelSnapshot): number {
  let max = 0
  for (const slotStr of Object.keys(snap)) {
    const n = Number(slotStr)
    if (Number.isFinite(n) && n > max) max = n
  }
  return max
}

function groupChannelPendingByChannel(
  pending: Map<string, PendingDrop>,
): Map<string, Array<{ key: string; drop: PendingDrop }>> {
  const out = new Map<string, Array<{ key: string; drop: PendingDrop }>>()
  for (const [key, drop] of pending) {
    if (drop.target.type !== 'channel') continue
    const list = out.get(drop.target.channelId) ?? []
    list.push({ key, drop })
    out.set(drop.target.channelId, list)
  }
  return out
}

interface AppendOutcome {
  ok: boolean
  /** May be undefined if backend response omitted link_id (insert still counts as success). */
  linkId?: string
}

async function appendOneInsert(
  productId: string,
  channelId: string,
  drop: PendingDrop,
  tempSlot: number,
): Promise<AppendOutcome> {
  try {
    const assetId = await ensureAssetId(drop.image, productId)
    if (!assetId) return { ok: false }
    const l = await linkAssetToChannel(productId, channelId, assetId, tempSlot)
    if (!l?.success) return { ok: false }
    return { ok: true, linkId: l.link_id }
  } catch {
    return { ok: false }
  }
}

/**
 * Step 2 of the new pipeline: append each pending channel-drop at a TEMP slot
 * (= maxExistingSlot + 1 + i) and remember the new link_id per pending key.
 * Returns counts for the toast + the map for the reorder step.
 */
export async function runChannelInsertsAppend(
  ctx: ChannelInsertContext,
): Promise<ChannelInsertResult> {
  const newLinkIds = new Map<string, string>()
  let ok = 0
  let err = 0
  const grouped = groupChannelPendingByChannel(ctx.pending)
  for (const [channelId, drops] of grouped) {
    const snap = ctx.snapshot.get(channelId) ?? {}
    const base = maxSlot(snap)
    for (let i = 0; i < drops.length; i++) {
      const { key, drop } = drops[i]
      const hasSnapshot = ctx.snapshot.has(channelId)
      // No snapshot for this channel → can't reason about collisions, fall
      // back to the user's target slot directly (mirrors legacy behaviour for
      // unit tests that exercise the link path without seeding a snapshot).
      const target =
        drop.target.type === 'channel' ? drop.target.slot : base + 1 + i
      const tempSlot = hasSnapshot ? base + 1 + i : target
      const outcome = await appendOneInsert(ctx.productId, channelId, drop, tempSlot)
      if (outcome.ok) {
        if (outcome.linkId) newLinkIds.set(key, outcome.linkId)
        ok++
      } else {
        err++
      }
    }
  }
  return { newLinkIds, ok, err }
}

export interface ChannelReorderContext {
  productId: string
  pending: Map<string, PendingDrop>
  shifts: PendingShift[]
  snapshot: CurrentSlotsByChannel
}

function collectAffectedChannels(ctx: ChannelReorderContext): Set<string> {
  const out = new Set<string>()
  for (const s of ctx.shifts) out.add(s.channelId)
  for (const d of ctx.pending.values()) {
    if (d.target.type === 'channel') out.add(d.target.channelId)
  }
  return out
}

function insertsForChannel(
  channelId: string,
  pending: Map<string, PendingDrop>,
  newLinkIds: Map<string, string>,
): ChannelInsert[] {
  const out: ChannelInsert[] = []
  for (const [key, drop] of pending) {
    if (drop.target.type !== 'channel' || drop.target.channelId !== channelId) continue
    const linkId = newLinkIds.get(key)
    if (!linkId) continue
    out.push({ targetSlot: drop.target.slot, linkId })
  }
  return out
}

/**
 * Step 3 of the new pipeline: per affected channel, build the final link-id
 * list (existing items moved by shifts + newly appended inserts at their
 * target slot) and call `reorderProductImages` once. Skips channels where no
 * reorder is needed (no shifts AND all inserts already at their target slot).
 */
export async function runChannelReorderFinal(
  ctx: ChannelReorderContext,
  newLinkIds: Map<string, string>,
): Promise<void> {
  const channels = collectAffectedChannels(ctx)
  for (const channelId of channels) {
    const snap = ctx.snapshot.get(channelId) ?? {}
    const channelShifts = ctx.shifts.filter((s) => s.channelId === channelId)
    const inserts = insertsForChannel(channelId, ctx.pending, newLinkIds)
    const finalLinkIds = buildFinalLinkIds(channelShifts, snap, inserts, maxSlot(snap))
    if (finalLinkIds.length === 0) continue
    try {
      await reorderProductImages(ctx.productId, finalLinkIds, channelId)
    } catch {
      // swallow — surfaced via finalize toast aggregation
    }
  }
}
